import React, {ReactNode} from "react";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import Translate from "../../shared/translation/Translate";
import Helper from "../../shared/components/Helper";
import Immutable from "../../shared/immutable/Immutable";
import {
	ConfigurationType,
	Connector,
	ConnectorInterface,
	ConnectorKeyStore,
	ConnectorProtocol,
	ConnectorServerCheck,
	ConnectorServerCheckInterface,
	ConnectorsServer,
	Host,
	KeystoreSSL,
	Server,
	ServerCheckMode,
	ServerInterface,
	SSL,
	SSLInterface,
	SSLProtocol,
	TrustStoreServerCheck,
	TrustStoreServerCheckInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import ConnectionFields from "../dialog/field/ConnectionFields";
import Administration from "../session/Administration";
import Message from "../../shared/log/Message";
import {MessageType} from "../../shared/log/MessageInterface";
import ConnectionCertificateDialog from "../dialog/components/ConnectionCertificateDialog";
import Info, {InfoType} from "../../shared/components/Info";
import UrlHelper from "../../shared/helper/UrlHelper";
import {InputText} from "primereact/inputtext";
import {Accordion, AccordionTab} from "primereact/accordion";
import Icon from "../../shared/components/Icon";
import MenuButton from "../../shared/menu/MenuButton";
import {InputSwitch} from "primereact/inputswitch";
import {KeystoreType} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Administration";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";

export interface ConnectionPanelProperties extends AbstractPanelProperties {
}

export interface ConnectionPanelState extends AbstractPanelState {
	parameter?: Immutable<ServerInterface>;
	originalParameter?: Immutable<ServerInterface>;
	showCertificateDialog: boolean;
	certificateParameter?: KeystoreSSL;
	selected?: number;
	fieldErrors: Map<ConnectionFields, string>;
	originalKeystores: Immutable<{ [key: string]: ConnectorKeyStore; }>;
	keystores: Immutable<{ [key: string]: ConnectorKeyStore; }>;
}

export class ConnectionPanelDefinition extends AbstractPanel<ConnectionPanelProperties, ConnectionPanelState> {
	constructor(props: Readonly<ConnectionPanelProperties>) {
		super(props);

		this.state = {
			...super.state,
			showCertificateDialog: false,
			fieldErrors: new Map<ConnectionFields, string>()
		};
	}

	private updateParameter() {
		this.setState({
			parameter: FunctionHelper.applyValue(this.props.serverConfig, this.state.parameter),
			originalParameter: this.props.serverConfig
		});
	}

	protected hasChanges(): boolean {
		let parameter: Server | undefined = Server.fromJson(this.state.parameter?.get());
		let originalParameter: Server | undefined = Server.fromJson(this.state.originalParameter?.get());

		if (!FunctionHelper.exists(originalParameter)) {
			return FunctionHelper.exists(parameter);
		}

		let originalKeystores: Immutable<{ [key: string]: ConnectorKeyStore; }> = this.state.originalKeystores;
		let keystores: Immutable<{ [key: string]: ConnectorKeyStore; }> = this.state.keystores;
		if (!keystores.equals(originalKeystores)) {
			return true;
		}

		return !Immutable.from(parameter.connectors.toJson()).equals(
			Immutable.from(originalParameter.connectors.toJson())
		) || !Immutable.from(parameter.host.toJson()).equals(
			Immutable.from(originalParameter.host.toJson())
		);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		this.updateParameter();

		let administration: Administration = this.props.context.session.getAdministration();
		let keystores: { [key: string]: ConnectorKeyStore; } =
			await administration.getKeystore(KeystoreType.Connector) as { [key: string]: ConnectorKeyStore; };

		this.setState({
			originalKeystores: Immutable.from(keystores),
			keystores: Immutable.from(keystores)
		});
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<ConnectionPanelProperties>, prevState: Readonly<ConnectionPanelState>, snapshot?: any
	): Promise<void> {
		if (this.props.contentMode !== prevProps.contentMode ||
			!prevProps.serverConfig?.equals(this.props.serverConfig)) {
			this.updateParameter();
		}
	}

	private computeFreePort(port?: number): number {
		let result: number = FunctionHelper.applyValue(port, Connector.getPortDefault());
		let parameter: Server | undefined = Server.fromJson(this.state.parameter?.get());

		if (!FunctionHelper.exists(parameter)) {
			return result;
		}

		for (let connector of FunctionHelper.applyValue(
			parameter?.connectors.connector, ConnectorsServer.getConnectorDefault()
		)) {
			if (connector.port === result) {
				return this.computeFreePort(result + 1);
			}
		}

		return result;
	}

	private async onChange(field: ConnectionFields, value: { port?: number, value?: any }): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let parameter: Server | undefined = Server.fromJson(this.state.parameter?.get());
		let errors: Map<ConnectionFields, string> = this.state.fieldErrors;

		let connectorConfig: Connector | undefined;
		let connectorIndex: number = 0;
		let connectors: Array<Connector> = FunctionHelper.applyValue(
			parameter.connectors.connector, ConnectorsServer.getConnectorDefault()
		);
		if (FunctionHelper.exists(value.port)) {
			connectorConfig = connectors.find(
				(connector: Connector) => connector.port === value.port
			);
			connectorIndex = connectors.findIndex(
				(connector: Connector) => connector.port === value.port
			);
		}

		switch (field) {
			case ConnectionFields.HOST:
				if (!FunctionHelper.exists(value.value) || FunctionHelper.isEmpty(value.value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.host.name = value.value;
				break;
			case ConnectionFields.PATH:
				if (!FunctionHelper.exists(value.value) || FunctionHelper.isEmpty(value.value)) {
					errors.set(field, "errors.fields.set");
				} else if (!UrlHelper.isUrlPath(value.value)) {
					errors.set(field, "errors.fields.url.path");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.host.contextPath = value.value;
				break;
			case ConnectionFields.ENABLED:
				connectorConfig!.enabled = value.value;
				break;
			case ConnectionFields.IP:
				connectorConfig!.address = value.value;
				break;
			case ConnectionFields.PORT:
				for (let connector of FunctionHelper.applyValue(
					parameter.connectors.connector, ConnectorsServer.getConnectorDefault()
				)) {
					if (connector.port === value.value) {
						await this.onChange(field, {port: value.port, value: value.value + 1});
						return;
					}
				}

				connectorConfig!.port = value.value;
				break;
			case ConnectionFields.PROTOCOL:
				connectorConfig!.protocol = value.value;
				break;
			case ConnectionFields.SSL:
				connectorConfig!.ssl = value.value ? SSL.fromJson({
					protocols: {
						protocol: [
							SSLProtocol.TLSv1,
							SSLProtocol.TLSv11,
							SSLProtocol.TLSv12,
							SSLProtocol.TLSv13
						]
					}
				} as SSLInterface) : undefined;
				break;
			case ConnectionFields.SSL_PROTOCOL:
				connectorConfig!.ssl!.protocols.protocol = value.value;
				break;
			case ConnectionFields.DELETE:
				parameter.connectors.connector!.splice(connectorIndex, 1);
				break;
			case ConnectionFields.CERTIFICATE:
				let tempKeystore: KeystoreSSL | undefined = connectorConfig?.ssl?.keystore?.clone();
				parameter.connectors.connector![connectorIndex].ssl!.keystore = value.value;

				let serverConfiguration: Server = (
					await administration.getConfiguration(ConfigurationType.Server) as Server
				).clone();
				serverConfiguration.host = parameter.host;
				serverConfiguration.connectors = parameter.connectors;

				try {
					await administration.validateConfiguration(
						ConfigurationType.Server, serverConfiguration, [
							TrustStoreServerCheck.fromJson({
								checkType: ServerCheckMode.Connector
							} as TrustStoreServerCheckInterface)
						]
					);
				} catch (e: any) {
					// reset keystore parameter
					parameter.connectors.connector![connectorIndex].ssl!.keystore = tempKeystore!;
					throw e;
				}

				let connectorKeyStores: { [key: string]: ConnectorKeyStore; } =
					await administration.getKeystore(KeystoreType.Connector) as { [key: string]: ConnectorKeyStore; };

				this.setState({
					keystores: Immutable.from(connectorKeyStores)
				})
				break;
			case ConnectionFields.THREADS:
				connectorConfig!.ssl!.maxThreads = value.value;
				break;
			case ConnectionFields.ADD:
				let port: number = this.computeFreePort();

				parameter.connectors.connector!.push(Connector.fromJson({
					port: port
				} as ConnectorInterface));
				break;
		}

		this.setState({
			parameter: Immutable.from(parameter.toJson() as ServerInterface),
		}, () => this.setState({
			hasChanges: this.hasChanges()
		}));
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: Server | undefined = Server.fromJson(this.state.parameter?.get());
		let administration: Administration = this.props.context.session.getAdministration();

		let serverConfiguration: Server = (
			await administration.getConfiguration(ConfigurationType.Server) as Server
		).clone();
		serverConfiguration.host = parameter!.host;
		serverConfiguration.connectors = parameter!.connectors;

		try {
			await administration.updateConfiguration(ConfigurationType.Server, serverConfiguration,
				[
					ConnectorServerCheck.fromJson({
						checkType: ServerCheckMode.Connector
					} as ConnectorServerCheckInterface)
				]
			);
			await administration.updateServerStatus();

			this.setState({
				submitted: false,
				hasChanges: false,
				parameter: this.state.originalParameter
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.connection", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
	}

	private async reset(): Promise<void> {
		this.setState({
			exception: undefined,
			hasChanges: false,
			parameter: this.state.originalParameter
		});
	}

	private renderConnection(connector: Connector, key: number, activePort: number | undefined): ReactNode {
		let sslIcon: Array<string> = ["icon-shield"];
		let sslTooltip: string;
		if (!FunctionHelper.exists(connector.ssl)) {
			sslIcon.push("stop");
			sslTooltip = Translate.value(
				"adminscreen.panels.connection.ssl.tooltip.disabled", {ns: "screen"}
			);
		} else {
			sslIcon.push("ok");
			sslTooltip = Translate.value(
				"adminscreen.panels.connection.ssl.tooltip.active", {ns: "screen"}
			);
		}

		let statusIcon: Array<string> = [];
		let statusTooltip: string;
		if (connector.enabled) {
			statusIcon.push("icon-play");
			statusTooltip = Translate.value(
				"adminscreen.panels.connection.enabled.active", {ns: "screen"}
			);
		} else {
			statusIcon.push("icon-pause");
			statusTooltip = Translate.value(
				"adminscreen.panels.connection.enabled.disabled", {ns: "screen"}
			);
		}

		return (
			<AccordionTab key={key}
						  headerTemplate={
							  <>
								  <Icon icon={statusIcon.join(" ")} tooltip={statusTooltip}/>
								  {FunctionHelper.applyStringValue(connector.address, "*")}
								  {FunctionHelper.exists(connector.port) && ":" + connector.port}
								  {FunctionHelper.exists(connector.protocol) && " (" + connector.protocol + ")"}
								  <Icon icon={sslIcon.join(" ")} tooltip={sslTooltip} position="left"/>
								  <MenuButton {...this.props} icon="icon icon-close" tooltipOptions={{position: "left"}}
											  disabled={activePort === connector.port}
											  tooltip={Translate.value(
												  "adminscreen.panels.connection.delete", {ns: "screen"}
											  )}
											  onClick={async (event) => {
												  event.preventDefault();
												  event.stopPropagation();
												  await this.onChange(
													  ConnectionFields.DELETE, {port: connector.port}
												  );
											  }}/>
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor={"control-active-" + key}>
							<Translate {...this.props} data="adminscreen.panels.connection.active" namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={Connector.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId={"control-active-" + key} disabled={activePort === connector.port}
										 checked={FunctionHelper.applyValue(
											 connector.enabled, Connector.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConnectionFields.ENABLED, {port: connector.port, value: e.value}
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6"/>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor={"control-ip-" + key}>
							<Translate {...this.props} data="adminscreen.panels.connection.ip" namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={Connector.getAddressDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id={"control-ip-" + key}
									   value={FunctionHelper.applyValue(
										   connector.address, Connector.getAddressDefault()
									   )}
									   onInput={(e) => this.onChange(
										   ConnectionFields.IP, {
											   port: connector.port, value: e.currentTarget.value
										   }
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor={"control-port-" + key}>
							<Translate {...this.props} data="adminscreen.panels.connection.port" namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={Connector.getPortDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId={"control-port-" + key} step={1} showButtons={true}
										 value={FunctionHelper.applyValue(connector.port, Connector.getPortDefault())}
										 min={Connector.getPortMin()}  useGrouping={false}
										 onChange={(e) => this.onChange(
											 ConnectionFields.PORT, {port: connector.port, value: e.value}
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor={"control-protocol-" + key}>
							<Translate {...this.props} data="adminscreen.panels.connection.protocol.label"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={Connector.getProtocolDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId={"control-protocol-" + key}
									  value={FunctionHelper.applyValue(
										  connector.protocol, Connector.getProtocolDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "adminscreen.panels.connection.protocol.ajp13",
												  {ns: "screen"}
											  ),
											  value: ConnectorProtocol.AJP13
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.connection.protocol.http11",
												  {ns: "screen"}
											  ),
											  value: ConnectorProtocol.HTTP11
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.connection.protocol.http20",
												  {ns: "screen"}
											  ),
											  value: ConnectorProtocol.HTTP20
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConnectionFields.PROTOCOL, {port: connector.port, value: e.value}
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor={"control-ssl-" + key}>
							<Translate {...this.props} data="adminscreen.panels.connection.ssl.enabled"
									   namespace="screen"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId={"control-ssl-" + key} checked={FunctionHelper.exists(connector.ssl)}
										 onChange={(e) => this.onChange(
											 ConnectionFields.SSL, {
												 port: FunctionHelper.applyValue(
													 connector.port, Connector.getPortDefault()
												 ),
												 value: e.value
											 }
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6"/>
					{FunctionHelper.exists(connector.ssl) &&
						<>
							<div className="p-col-12 p-lg-6 p-grid">
								<label className="p-col-12 p-sm-5" htmlFor={"control-ssl-protocol-" + key}>
									<Translate {...this.props} data="adminscreen.panels.connection.ssl.protocol.label"
											   namespace="screen"/>
									<Info {...this.props} type={InfoType.Help} markdown={true}
										  data={Connector.getProtocolDescription()}/>
								</label>
								<div className="p-col-12 p-sm-7">
									<MultiSelect inputId={"control-ssl-protocol-" + key} display="comma"
												 value={FunctionHelper.applyValue(
													 connector.ssl?.protocols?.protocol, []
												 )}
												 onChange={(e) => this.onChange(
													 ConnectionFields.SSL_PROTOCOL, {
														 port: connector.port,
														 value: e.value
													 }
												 )}
												 options={[
													 {
														 label: Translate.value(
															 "adminscreen.panels.connection.ssl.protocol.tlsv1",
															 {ns: "screen"}
														 ),
														 value: SSLProtocol.TLSv1
													 },
													 {
														 label: Translate.value(
															 "adminscreen.panels.connection.ssl.protocol.tlsv11",
															 {ns: "screen"}
														 ),
														 value: SSLProtocol.TLSv11
													 },
													 {
														 label: Translate.value(
															 "adminscreen.panels.connection.ssl.protocol.tlsv12",
															 {ns: "screen"}
														 ),
														 value: SSLProtocol.TLSv12
													 },
													 {
														 label: Translate.value(
															 "adminscreen.panels.connection.ssl.protocol.tlsv13",
															 {ns: "screen"}
														 ),
														 value: SSLProtocol.TLSv13
													 }
												 ]}/>
								</div>
							</div>
							<div className="p-col-12 p-lg-6 p-grid">
								<label className="p-col-12 p-sm-5" htmlFor={"control-ssl-alias-" + key}>
									<Translate {...this.props} data="adminscreen.panels.connection.ssl.alias"
											   namespace="screen"/>
									<Info {...this.props} type={InfoType.Help} markdown={true}
										  data={KeystoreSSL.getAliasDescription()}/>
								</label>
								<div className="p-col-12 p-sm-7">
									<InputText id={"control-ssl-alias-" + key} disabled={true}
											   value={FunctionHelper.applyValue(
												   connector.ssl?.keystore?.alias, KeystoreSSL.getAliasDefault()
											   )}/>
								</div>
							</div>
							<div className="p-col-12 p-lg-6 p-grid">
								<label className="p-col-12 p-sm-5" htmlFor={"control-ssl-threads-" + key}>
									<Translate {...this.props} data="adminscreen.panels.connection.ssl.threads"
											   namespace="screen"/>
									<Info {...this.props} type={InfoType.Help} markdown={true}
										  data={SSL.getMaxThreadsDescription()}/>
								</label>
								<div className="p-col-12 p-sm-7">
									<InputNumber inputId={"control-ssl-threads-" + key} step={1} showButtons={true}
												 min={SSL.getMaxThreadsMin()}  useGrouping={false}
												 value={FunctionHelper.applyValue(
													 connector.ssl?.maxThreads, SSL.getMaxThreadsDefault()
												 )}
												 onChange={(e) => this.onChange(
													 ConnectionFields.THREADS,
													 {port: connector.port, value: e.value}
												 )}/>
								</div>
							</div>
							<div className="p-col-12 p-lg-6"/>
							<div className="p-col-12 p-lg-6 p-grid">
								<label className="p-col-12 p-sm-5"></label>
								<div className="p-col-12 p-sm-7">
									<MenuButton {...this.props} icon="icon icon-certificate" className="dialog-button"
												label={Translate.value(
													"adminscreen.panels.connection.certificate",
													{ns: "screen"}
												)}
												onClick={async () => {
													let certificateParameter: KeystoreSSL | undefined =
														connector.ssl?.keystore;
													if (!FunctionHelper.exists(certificateParameter)) {
														certificateParameter = KeystoreSSL.fromJson({});
													}

													this.setState({
														selected: connector.port,
														showCertificateDialog: true,
														certificateParameter: certificateParameter
													});
												}}/>
								</div>
							</div>
						</>
					}
				</fieldset>
			</AccordionTab>
		)
	}

	private renderContents(): ReactNode {
		let parameter: Server | undefined = Server.fromJson(this.state.parameter?.get());
		let errors: Map<ConnectionFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let activePort: number | undefined;
		let connectors: Array<Connector> = FunctionHelper.applyValue(
			parameter?.connectors?.connector, ConnectorsServer.getConnectorDefault()
		);
		for (let connector of connectors) {
			if (!connector.enabled) {
				continue;
			}

			// there are multiple active ports
			if (FunctionHelper.exists(activePort)) {
				activePort = undefined;
				break;
			}

			activePort = connector.port;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-host">
							<Translate {...this.props} data="adminscreen.panels.connection.host" namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={Host.getNameDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-host"
									   value={FunctionHelper.applyValue(parameter?.host.name, Host.getNameDefault())}
									   className={errors.has(ConnectionFields.HOST) ? "p-invalid" : ""}
									   tooltip={errors.has(ConnectionFields.HOST) ? Translate.value(
										   errors.get(ConnectionFields.HOST)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   ConnectionFields.HOST, {value: e.currentTarget.value}
									   )}/>
							{this.renderFieldError(errors.has(ConnectionFields.HOST) ? Translate.value(
								errors.get(ConnectionFields.HOST)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-path">
							<Translate {...this.props} data="adminscreen.panels.connection.path" namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={Host.getContextPathDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-path"
									   value={FunctionHelper.applyValue(
										   parameter?.host?.contextPath, Host.getContextPathDefault()
									   )}
									   className={errors.has(ConnectionFields.PATH) ? "p-invalid" : ""}
									   tooltip={errors.has(ConnectionFields.PATH) ? Translate.value(
										   errors.get(ConnectionFields.PATH)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   ConnectionFields.PATH, {value: e.currentTarget.value}
									   )}/>
							{this.renderFieldError(errors.has(ConnectionFields.PATH) ? Translate.value(
								errors.get(ConnectionFields.PATH)!, {ns: "message"}
							) : "")}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12">
						<Accordion>
							{connectors.map((connector: Connector, index: number) => {
								return this.renderConnection(connector, index, activePort)
							})}
						</Accordion>
					</div>
					<div className="p-col-12">
						<MenuButton {...this.props} className="dialog-button" icon="icon icon-add"
									label={Translate.value(
										"adminscreen.panels.connection.add", {ns: "screen"}
									)}
									onClick={() => this.onChange(ConnectionFields.ADD, {})}/>
					</div>
				</fieldset>
			</>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<>
				<div className="screen admin-connection-panel" data-help="global.screen.admin.content"
					 data-position="top">
					<div className="toolbar" hidden={!this.state.hasChanges}>
						<p>
							<Translate {...this.props} data="adminscreen.panels.webservices.changes"
									   namespace="screen"/>
						</p>
						<div className="buttons">
							<MenuButton {...this.props} label={Translate.value("buttons.save")}
										disabled={!this.state.hasChanges || this.state.submitted}
										icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
										className="primary-button" onClick={() => this.submit()}/>
							<MenuButton {...this.props} label={Translate.value("buttons.reset")}
										disabled={!this.state.hasChanges || this.state.submitted}
										icon={"icon icon-rotate-270 small"}
										className="dialog-button" onClick={() => this.reset()}/>
						</div>
					</div>
					<div className="body">
						<h1>
							<Translate {...this.props} data={"adminscreen.panels.connection.title"} namespace="screen"/>
							{this.renderHelpButton()}
						</h1>
						<Helper {...this.props}>
							<Translate {...this.props} data={"global.screen.admin.connection.general"}
									   namespace={"help"} markdown={true}/>
						</Helper>
						<fieldset className="p-col-12 p-grid">
							{this.renderError()}
						</fieldset>
						{this.renderContents()}
					</div>
				</div>
				<ConnectionCertificateDialog {...this.props}
											 visible={FunctionHelper.applyValue(
												 this.state.showCertificateDialog, false
											 )}
											 hide={() => this.setState({showCertificateDialog: false})}
											 onError={this.onError} parameter={this.state.certificateParameter}
											 submit={async (value?: KeystoreSSL) => {
												 await this.onChange(
													 ConnectionFields.CERTIFICATE,
													 {port: this.state.selected, value: value}
												 );
											 }}/>
			</>
		);
	}
}

const ConnectionPanel = React.memo(ConnectionPanelDefinition,
	(prevProps: Readonly<ConnectionPanelProperties>, nextProps: Readonly<ConnectionPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!prevProps.serverConfig?.equals(nextProps.serverConfig)) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}
		return true;
	}
);

export default ConnectionPanel;